import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCogs, faFlask, faClipboardList, faVial, faChartLine,
  faFileUpload, faMicrochip, faSearch, faFileAlt, faUsers,
  faProjectDiagram, faEye, faCloud, faTasks, faRobot,
  faDatabase, faMobileAlt, faShieldAlt, faRocket, faHdd, faCheck, faBolt
} from '@fortawesome/free-solid-svg-icons';
import { BarChart, Bar, Cell, LabelList, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, LineChart, Line, CartesianGrid } from 'recharts';
import { motion } from 'framer-motion';
import AboutContentHeader from './AboutContentHeader';
import './AboutTemplate.css';

// 2. Create a reusable animated section wrapper
const AnimatedSection = ({ children, delay = 0 }) => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
    >
      {children}
    </motion.div>
  );
};

// Dynamic component renderers
const HeroImageSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-hero-image-section`}>
    <div className={`${platformKey}-hero-content`}>
      <div className={`${platformKey}-hero-text`}>
        <h2>{section.title}</h2>
        <p>{section.content}</p>
      </div>
      <div className={`${platformKey}-hero-image`}>
        {section.image ? (
          <img src={section.image} alt={section.title} />
        ) : (
          <div className={`${platformKey}-image-placeholder`}>
            <span>📷</span>
            <p>Image not available</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const SideBySideSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-side-by-side-section ${section.alignment === 'left' ? 'image-left' : 'image-right'}`}>
    <div className={`${platformKey}-side-content`}>
      <div className={`${platformKey}-side-image`}>
        {section.image ? (
          <img src={section.image} alt={section.title} />
        ) : (
          <div className={`${platformKey}-image-placeholder`}>
            <span>📷</span>
            <p>Image not available</p>
          </div>
        )}
      </div>
      <div className={`${platformKey}-side-text`}>
        <h2>{section.title}</h2>
        <p>{section.content}</p>
      </div>
    </div>
  </div>
);

const FeaturesGridSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-features-grid-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-features-grid`}>
      {section.items.map((item, index) => (
        <div key={index} className={`${platformKey}-feature-item`}>
          <div className={`${platformKey}-feature-image`}>
            {item.image ? (
              <img src={item.image} alt={item.title} />
            ) : (
              <div className={`${platformKey}-image-placeholder`}>
                <span>{item.icon || '📷'}</span>
              </div>
            )}
          </div>
          <div className={`${platformKey}-feature-content`}>
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const StatsShowcaseSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-stats-showcase-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-stats-grid`}>
      {section.stats.map((stat, index) => (
        <div key={index} className={`${platformKey}-stat-item`}>
          <div className={`${platformKey}-stat-icon`}>
            <FontAwesomeIcon icon={iconMap[stat.icon] || iconMap['default']} />
          </div>
          <div className={`${platformKey}-stat-value`}>{stat.value}</div>
          <div className={`${platformKey}-stat-label`}>{stat.label}</div>
        </div>
      ))}
    </div>
  </div>
);

const iconMap = {
  // Emojis from data file
  '📁': faFileUpload,
  '⚡': faBolt,
  '🔍': faSearch,
  '📊': faChartLine,
  '👥': faUsers,
  '🔧': faProjectDiagram,
  '👁️': faEye,
  '🧪': faVial,
  '🤖': faRobot,
  '☁️': faCloud,
  '📄': faFileAlt,
  '🚀': faRocket,
  '💾': faHdd,
  '✓': faCheck,
  '📈': faChartLine,
  '🔬': faVial,

  // String keys for flexibility
  'cogs': faCogs,
  'flask': faFlask,
  'clipboard': faClipboardList,
  'vial': faVial,
  'chart-line': faChartLine,
  'tasks': faTasks,
  'database': faDatabase,
  'mobile': faMobileAlt,
  'security': faShieldAlt,
  'rocket': faRocket,
  'bolt': faBolt,
  'hdd': faHdd,
  'check': faCheck,

  // Default
  'default': faCogs
};

const WorkflowStepsSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-workflow-steps-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-workflow-steps`}>
      {section.steps.map((step, index) => (
        <div key={index} className={`${platformKey}-workflow-step`}>
          <div className={`${platformKey}-step-number`}>{step.step}</div>
          <div className={`${platformKey}-step-icon`}>
            <FontAwesomeIcon icon={iconMap[step.icon] || iconMap['default']} />
          </div>
          <div className={`${platformKey}-step-content`}>
            <h3>{step.title}</h3>
            <p>{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TechSpecsSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-tech-specs-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-tech-specs-grid`}>
      {section.specs.map((spec, index) => (
        <div key={index} className={`${platformKey}-spec-item`}>
          <div className={`${platformKey}-spec-label`}>{spec.label}</div>
          <div className={`${platformKey}-spec-value`}>{spec.value}</div>
        </div>
      ))}
    </div>
  </div>
);

const ComparisonTableSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-comparison-table-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-comparison-table`}>
      <div className={`${platformKey}-comparison-header`}>
        <div>Feature</div>
        <div>Traditional</div>
        <div>{section.title.split(' ')[2] || 'Our Platform'}</div>
        <div>Advantage</div>
      </div>
      {section.comparisons.map((comparison, index) => (
        <div key={index} className={`${platformKey}-comparison-row`}>
          <div className={`${platformKey}-comparison-feature`}>{comparison.feature}</div>
          <div className={`${platformKey}-comparison-traditional`}>{comparison.traditional}</div>
          <div className={`${platformKey}-comparison-ours`}>{comparison.familyrisk || comparison.newbornrisk || comparison.ours}</div>
          <div className={`${platformKey}-comparison-advantage`}>{comparison.advantage}</div>
        </div>
      ))}
    </div>
  </div>
);

const DataVisualizationSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-data-visualization-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-visualization-grid`}>
      {section.visualizations.map((viz, index) => (
        <div key={index} className={`${platformKey}-visualization-item`}>
          <h3>{viz.title}</h3>
          {viz.type === 'pie-chart' ? (
            <div className={`${platformKey}-pie-chart`}>
              <div className={`${platformKey}-pie-chart-placeholder`}>
                <span>📊</span>
                <p>Pie Chart: {viz.title}</p>
              </div>
              <div className={`${platformKey}-pie-legend`}>
                {viz.data.map((item, i) => (
                  <div key={i} className={`${platformKey}-legend-item`}>
                    <span className={`${platformKey}-legend-color`} style={{ backgroundColor: item.color }}></span>
                    <span>{item.label}: {item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`${platformKey}-bar-chart`}>
              <div className={`${platformKey}-bar-chart-placeholder`}>
                <span>📈</span>
                <p>Bar Chart: {viz.title}</p>
              </div>
              <div className={`${platformKey}-bar-data`}>
                {viz.data.map((item, i) => (
                  <div key={i} className={`${platformKey}-bar-item`}>
                    <span>{item.label}: {item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const MobileShowcaseSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-mobile-showcase-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-mobile-screenshots`}>
      {section.screenshots.map((screenshot, index) => (
        <div key={index} className={`${platformKey}-screenshot-item`}>
          <div className={`${platformKey}-phone-mockup`}>
            <div className={`${platformKey}-phone-screen`}>
              <span>📱</span>
              <p>Screenshot not available</p>
            </div>
          </div>
          <div className={`${platformKey}-screenshot-info`}>
            <h3>{screenshot.title}</h3>
            <p>{screenshot.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AppFeaturesSection = ({ section, platformKey }) => (
  <div className={`${platformKey}-app-features-section`}>
    <h2>{section.title}</h2>
    <div className={`${platformKey}-app-features-grid`}>
      {section.features.map((feature, index) => (
        <div key={index} className={`${platformKey}-app-feature-item`}>
          <div className={`${platformKey}-app-feature-icon`}>
            <span>{feature.icon}</span>
          </div>
          <div className={`${platformKey}-app-feature-content`}>
            <h3>{feature.title}</h3>
            <p>{feature.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Benefits Chart Component
const BenefitsChart = ({ platformKey }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const comparisonData = [
    { metric: 'Analysis Speed', unit: 'higher is better', traditional: 35, ourPlatform: 90 },
    { metric: 'Diagnostic Accuracy', unit: 'higher is better', traditional: 72, ourPlatform: 94 },
    { metric: 'Cost Efficiency', unit: 'higher is better', traditional: 40, ourPlatform: 85 },
    { metric: 'Data Throughput', unit: 'higher is better', traditional: 50, ourPlatform: 95 },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % comparisonData.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [comparisonData.length]);

  const activeMetric = comparisonData[activeIndex];

  const dataForChart = [
    { name: 'Traditional', value: activeMetric.traditional },
    { name: 'Our Platform', value: activeMetric.ourPlatform }
  ];

  return (
    <div className={`${platformKey}-about-chart-container`}>
      <h3 className={`${platformKey}-chart-title`}>
        {activeMetric.metric}
      </h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart 
          data={dataForChart} 
          layout="vertical" 
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
        >
          <XAxis type="number" hide domain={[0, 100]} />
          <YAxis 
            type="category" 
            dataKey="name" 
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'rgba(255, 255, 255, 0.8)', fontSize: 13 }}
            width={110}
          />
          <Bar dataKey="value" radius={[0, 5, 5, 0]}>
            <LabelList dataKey="value" position="right" style={{ fill: 'rgba(255,255,255,0.9)', fontSize: 12 }} formatter={(value) => `${value}%`} />
            {
              dataForChart.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.name === 'Our Platform' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.3)'} />
              ))
            }
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main component renderer
const renderContentSection = (section, platformKey) => {
  switch (section.type) {
    case 'hero-image':
      return <HeroImageSection section={section} platformKey={platformKey} />;
    case 'side-by-side':
      return <SideBySideSection section={section} platformKey={platformKey} />;
    case 'features-grid':
      return <FeaturesGridSection section={section} platformKey={platformKey} />;
    case 'stats-showcase':
      return <StatsShowcaseSection section={section} platformKey={platformKey} />;
    case 'workflow-steps':
      return <WorkflowStepsSection section={section} platformKey={platformKey} />;
    case 'tech-specs':
      return <TechSpecsSection section={section} platformKey={platformKey} />;
    case 'comparison-table':
      return <ComparisonTableSection section={section} platformKey={platformKey} />;
    case 'data-visualization':
      return <DataVisualizationSection section={section} platformKey={platformKey} />;
    case 'mobile-showcase':
      return <MobileShowcaseSection section={section} platformKey={platformKey} />;
    case 'app-features':
      return <AppFeaturesSection section={section} platformKey={platformKey} />;
    default:
      return null;
  }
};

const AboutTemplate = ({ 
  platformName, 
  subtitle, 
  overview, 
  headerImage,
  contentSections = [],
  benefits, 
  ctaText, 
  ctaLink, 
  platformKey 
}) => {
  const getUniqueClassName = (baseClass) => `${platformKey}-${baseClass}`;

  return (
    <div className={getUniqueClassName('about-container')}>
      <AboutContentHeader 
        platformName={platformName} 
        subtitle={subtitle} 
        overview={overview} 
        headerImage={headerImage} 
        platformKey={platformKey} 
      />

      <div className={getUniqueClassName('about-content')}>
        {/* Dynamic Content Sections */}
        {contentSections.map((section, index) => (
          <AnimatedSection key={index} delay={index * 0.1}>
            <section className={getUniqueClassName('dynamic-section')}>
              {renderContentSection(section, platformKey)}
            </section>
          </AnimatedSection>
        ))}

        {/* Benefits Section */}
        <AnimatedSection delay={contentSections.length * 0.1}>
          <section className={getUniqueClassName('about-benefits')}>
            <h2>Key Benefits</h2>
            <div className={getUniqueClassName('benefits-content')}>
              <div className={getUniqueClassName('benefits-left')}>
                <div className={getUniqueClassName('benefits-description')}>
                  <p>
                    Discover the comprehensive advantages that make {platformName} the leading choice 
                    for advanced genomic analysis and personalized healthcare solutions. Our platform 
                    combines cutting-edge technology with user-friendly design to deliver exceptional 
                    results for researchers, clinicians, and healthcare providers.
                  </p>
                </div>
                <div className={getUniqueClassName('benefits-chart')}>
                  <BenefitsChart platformKey={platformKey} />
                </div>
              </div>
              <ul className={getUniqueClassName('benefits-list')}>
                {benefits.slice(0, 4).map((benefit, index) => (
                  <li key={index}>
                    <p className={getUniqueClassName('benefit-text')}>{benefit}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection delay={(contentSections.length + 1) * 0.1}>
          <section className={getUniqueClassName('about-cta')}>
            <h2>Get Started</h2>
            <p>Ready to explore the full capabilities of {platformName}?</p>
            <Link to={ctaLink} className={getUniqueClassName('cta-button')}>{ctaText}</Link>
          </section>
        </AnimatedSection>
      </div>
    </div>
  );
};

export default AboutTemplate; 